import { useEffect, useState, Fragment } from "react";
import api, { price } from "../lib/api";
import DateRange, { rangeParams } from "../components/DateRange";
import { toast } from "sonner";
import { Search, Award, StickyNote, ChevronDown, ChevronRight, Trash2 } from "lucide-react";
import { ConfirmDialog } from "../components/ConfirmDialog";

export default function Suppliers() {
  const [products, setProducts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [suppliers, setSuppliers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [supProducts, setSupProducts] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const toggleSupplier = async (name) => {
    if (expanded === name) { setExpanded(null); return; }
    setExpanded(name);
    if (!supProducts[name]) {
      try { const { data } = await api.get("/supplier-products", { params: { supplier: name } }); setSupProducts((m) => ({ ...m, [name]: data })); }
      catch { setSupProducts((m) => ({ ...m, [name]: [] })); }
    }
  };

  const loadSuppliers = () => api.get("/suppliers").then(({ data }) => setSuppliers(data));
  const delSupplier = (name) => setConfirmDel(name);
  const doDelSupplier = async () => {
    try { await api.delete(`/suppliers/${encodeURIComponent(confirmDel)}`); toast.success("Fornitore eliminato"); loadSuppliers(); }
    catch { toast.error("Impossibile eliminare il fornitore"); }
    setConfirmDel(null);
  };
  const saveSupplierMeta = async (name, field, value) => {
    await api.put(`/suppliers/${encodeURIComponent(name)}/meta`, { [field]: value });
    toast.success("Note fornitore salvate");
    loadSuppliers();
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSuppliers(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get("/products").then(({ data }) => { const multi = data; setProducts(multi); if (multi.length) setSelected(multi[0]); }); }, []);
  useEffect(() => {
    if (!selected) return;
    api.get(`/suppliers/comparison/${encodeURIComponent(selected.normalized_name)}`, { params: rangeParams(from, to) }).then(({ data }) => setRows(data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, from, to]);

  const filtered = products.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  const best = rows.length ? rows[0].supplier : null;

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Analisi</p>
          <h1 className="font-serif text-4xl sm:text-5xl font-light tracking-tight text-stone-900">Confronto fornitori</h1>
          <p className="text-sm text-stone-500 mt-2">Scopri chi ti offre il miglior prezzo per lo stesso prodotto.</p>
        </div>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} onClear={() => { setFrom(""); setTo(""); }} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-stone-200 rounded-sm p-4 lg:max-h-[600px] flex flex-col">
          <div className="relative mb-3">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input data-testid="supplier-product-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca prodotto…"
              className="w-full border border-stone-300 bg-white pl-9 pr-3 py-2 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
          </div>
          <div className="overflow-y-auto flex-1 space-y-1">
            {filtered.map((p) => (
              <button key={p.normalized_name} data-testid={`sup-product-${p.normalized_name}`} onClick={() => setSelected(p)}
                className={`w-full text-left px-3 py-2.5 rounded-sm transition-colors ${
                  selected?.normalized_name === p.normalized_name ? "bg-brand text-white" : "hover:bg-stone-100 text-stone-700"
                }`}>
                <p className="text-sm font-medium truncate">{p.name}</p>
                <p className={`text-xs ${selected?.normalized_name === p.normalized_name ? "text-white/70" : "text-stone-400"}`}>{p.suppliers.length} fornitori</p>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-stone-200 rounded-sm overflow-hidden">
          <div className="p-6 border-b border-stone-200">
            <h3 className="font-serif text-3xl text-stone-900">{selected?.name || "—"}</h3>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
                <th className="text-left font-bold py-4 px-6">Fornitore</th>
                <th className="text-right font-bold py-4 px-6">Prezzo medio</th>
                <th className="text-right font-bold py-4 px-6">Min</th>
                <th className="text-right font-bold py-4 px-6">Max</th>
                <th className="text-right font-bold py-4 px-6">Ultimo</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={5} className="py-16 text-center text-stone-400">Nessun dato per questo prodotto.</td></tr>}
              {rows.map((r) => (
                <tr key={r.supplier} data-testid={`comparison-row-${r.supplier}`} className="border-b border-stone-100 hover:bg-stone-50 transition-colors">
                  <td className="py-4 px-6 font-semibold text-stone-800">
                    <span className="flex items-center gap-2">
                      {r.supplier === best && <Award size={16} className="text-brand-accent" />}
                      {r.supplier}
                    </span>
                  </td>
                  <td className={`py-4 px-6 text-right font-bold ${r.supplier === best ? "text-brand" : "text-stone-900"}`}>{price(r.avg_price)}</td>
                  <td className="py-4 px-6 text-right text-stone-500">{price(r.min_price)}</td>
                  <td className="py-4 px-6 text-right text-stone-500">{price(r.max_price)}</td>
                  <td className="py-4 px-6 text-right text-stone-700">{price(r.last_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {best && rows.length > 1 && (
            <div className="p-6 bg-brand/5 border-t border-stone-200 text-sm text-stone-700">
              <span className="font-semibold text-brand">{best}</span> offre il prezzo medio più conveniente per questo prodotto.
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-sm overflow-hidden">
        <div className="p-6 border-b border-stone-200 flex items-center gap-2">
          <StickyNote size={18} className="text-brand" strokeWidth={1.5} />
          <h3 className="font-serif text-2xl text-stone-900">Anagrafica &amp; note fornitori</h3>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-stone-200 text-xs uppercase tracking-[0.12em] text-stone-500">
              <th className="text-left font-bold py-4 px-6">Fornitore</th>
              <th className="text-right font-bold py-4 px-6">Fatture</th>
              <th className="text-right font-bold py-4 px-6">Spesa totale</th>
              <th className="text-left font-bold py-4 px-6 w-1/3">Note</th>
              <th className="text-left font-bold py-4 px-6 w-1/3">Condizioni di consegna</th>
              <th className="text-right font-bold py-4 px-6">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {suppliers.length === 0 && <tr><td colSpan={6} className="py-12 text-center text-stone-400">Nessun fornitore ancora.</td></tr>}
            {suppliers.map((s) => (
              <Fragment key={s.supplier_name}>
              <tr data-testid={`supplier-meta-${s.supplier_name}`} className="border-b border-stone-100 align-top">
                <td className="py-4 px-6 font-semibold text-stone-800">
                  <button data-testid={`supplier-expand-${s.supplier_name}`} onClick={() => toggleSupplier(s.supplier_name)} className="flex items-center gap-2 hover:text-brand transition-colors">
                    {expanded === s.supplier_name ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    {s.supplier_name}
                  </button>
                </td>
                <td className="py-4 px-6 text-right text-stone-600">{s.invoice_count}</td>
                <td className="py-4 px-6 text-right text-stone-700">{price(s.total_spent)}</td>
                <td className="py-4 px-6">
                  <textarea data-testid={`supplier-notes-${s.supplier_name}`} defaultValue={s.notes} rows={2} placeholder="Aggiungi note…"
                    onBlur={(e) => { if (e.target.value !== s.notes) saveSupplierMeta(s.supplier_name, "notes", e.target.value); }}
                    className="w-full border border-stone-300 bg-white px-2 py-1.5 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40 resize-none" />
                </td>
                <td className="py-4 px-6">
                  <input data-testid={`supplier-delivery-${s.supplier_name}`} defaultValue={s.delivery_conditions} placeholder="Es. franco magazzino, min 100€"
                    onBlur={(e) => { if (e.target.value !== s.delivery_conditions) saveSupplierMeta(s.supplier_name, "delivery_conditions", e.target.value); }}
                    className="w-full border border-stone-300 bg-white px-2 py-1.5 text-sm rounded-sm outline-none focus:ring-2 focus:ring-brand/40" />
                </td>
                <td className="py-4 px-6 text-right">
                  <button data-testid={`delete-supplier-${s.supplier_name}`} onClick={() => delSupplier(s.supplier_name)} className="text-stone-400 hover:text-alert transition-colors" title="Elimina fornitore"><Trash2 size={16} /></button>
                </td>
              </tr>
              {expanded === s.supplier_name && (
                <tr data-testid={`supplier-products-${s.supplier_name}`} className="border-b border-stone-100 bg-stone-50/60">
                  <td colSpan={6} className="px-6 py-4">
                    {!supProducts[s.supplier_name] ? <p className="text-sm text-stone-400">Caricamento…</p>
                      : supProducts[s.supplier_name].length === 0 ? <p className="text-sm text-stone-400">Nessun prodotto associato a questo fornitore nell'anagrafica.</p>
                      : (
                      <table className="w-full text-sm">
                        <thead><tr className="text-xs uppercase tracking-[0.1em] text-stone-400">
                          <th className="text-left font-bold py-2">Prodotto</th>
                          <th className="text-left font-bold py-2">Codice prodotto</th>
                          <th className="text-left font-bold py-2">Codice fornitore</th>
                          <th className="text-right font-bold py-2">Prezzo</th>
                        </tr></thead>
                        <tbody>
                          {supProducts[s.supplier_name].map((pr) => (
                            <tr key={pr.product_id} className="border-t border-stone-200">
                              <td className="py-2 text-stone-800">{pr.name}</td>
                              <td className="py-2 text-stone-500">{pr.code || "—"}</td>
                              <td className="py-2 font-medium text-stone-700">{pr.supplier_code || "—"}</td>
                              <td className="py-2 text-right text-stone-700">{pr.purchase_price != null ? price(pr.purchase_price) : "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog open={!!confirmDel} title="Elimina fornitore" description={confirmDel ? `Eliminare "${confirmDel}" dall'anagrafica?` : ""} confirmLabel="Elimina" onConfirm={doDelSupplier} onClose={() => setConfirmDel(null)} />
    </div>
  );
}
